import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { CheckCircleIcon, CloseIcon } from "../StrategyCanvasHelpers";
import {
  STRATEGY_CANVAS,
  STRATEGY_CANVAS_BRIEF,
  STRATEGY_CANVAS_VIEW,
} from "../../../../data/RouteUrls";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import useStrategyCanvasStore, {
  useStrategyCanvasStoreSelector,
} from "../_redux/StrategyCanvasStore";
import useStrategyCanvasReviewStore, {
  useStrategyCanvasReviewStoreSelector,
} from "../StrategyCanvasReview/_redux/StrategyCanvasReviewStore";
import {
  CanvasModeBody,
  StrategyCanvasBodySkeleton,
} from "../StrategyCanvasReview/CanvasMode/StrategyCanvasCanvasMode";
import "../StrategyCanvasReview/CanvasMode/StrategyCanvasCanvasMode.scss";
import "./StrategyCanvasView.scss";

const selectClients = (state) => state.clients;
const selectCanvasModeByUniqueId = (state) => state.canvasModeByUniqueId;
const selectCanvasModeLoading = (state) => state.canvasModeLoading;
const selectCanvasDownloading = (state) => state.canvasDownloading;

// Scoped per-canvas (not a single flat flag) - each client reaching a
// locked canvas is its own milestone, so a coach reviewing several clients
// should see the congratulatory banner once per client, not just once ever.
const getLockedBannerSeenKey = (id) => `scCanvasLockedBannerSeen_${id}`;

// Landed on from the dashboard when button.label === "View Strategy Canvas"
// (canvas_state LOCKED, section 3 of the integration doc) - a standalone,
// read-only view of the final locked canvas. Reuses the same canvas-mode
// data/rendering (section 6) as the Canvas Mode tab inside Review Mode, just
// without the Review Mode/Canvas Mode toggle since there's nothing left to
// review once a canvas is locked.
const StrategyCanvasView = () => {
  const history = useHistory();
  const { briefId } = useParams();
  const dispatch = useDispatch();

  const clients = useStrategyCanvasStoreSelector(selectClients);
  const client = (clients || []).find((sme) => sme.briefId === briefId);
  const companyName = client?.name || "";

  const canvasModeByUniqueId = useStrategyCanvasReviewStoreSelector(
    selectCanvasModeByUniqueId,
  );
  const canvasModeLoading = useStrategyCanvasReviewStoreSelector(
    selectCanvasModeLoading,
  );
  const canvasMode = canvasModeByUniqueId[briefId];
  const canvasDownloading = useStrategyCanvasStoreSelector(
    selectCanvasDownloading,
  );

  const handleDownloadCanvasPdf = () => {
    useStrategyCanvasStore.getState().downloadCanvasPdf(briefId);
  };

  const [showLockedBanner, setShowLockedBanner] = useState(false);
  useEffect(() => {
    const key = getLockedBannerSeenKey(briefId);
    if (!localStorage.getItem(key)) {
      setShowLockedBanner(true);
      localStorage.setItem(key, "true");
    }
  }, [briefId]);

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = "Art of Scale | Strategy Canvas";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Strategy Canvas" });
    if (!useStrategyCanvasStore.getState().clients) {
      useStrategyCanvasStore.getState().fetchDashboard();
    }
    useStrategyCanvasReviewStore.getState().fetchCanvasMode(briefId);
    // eslint-disable-next-line
  }, [briefId]);

  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(STRATEGY_CANVAS_VIEW, [
        breadCrumbsArray[STRATEGY_CANVAS_VIEW][0],
        {
          title: companyName,
          url: STRATEGY_CANVAS_BRIEF.replace(":briefId", briefId),
        },
        breadCrumbsArray[STRATEGY_CANVAS_VIEW][2],
      ]),
    );
    // eslint-disable-next-line
  }, [companyName, briefId]);

  return (
    <div>
      <ProjectHeader
        handleNavigation={() => history.push(STRATEGY_CANVAS)}
        showBack
      />
      <div id="strategy-canvas-view">
        <div className="scv-header">
          <div className="scv-header-left">
            <span className="scv-locked-badge">
              <i className="fa-solid fa-lock" />
              Canvas Locked
            </span>
            <div className="scv-header-title">
              Strategy Canvas for <strong>{companyName}</strong>
            </div>
          </div>
          <button
            type="button"
            className="scv-download-btn"
            onClick={handleDownloadCanvasPdf}
            disabled={canvasDownloading}
          >
            {canvasDownloading ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" />
                Downloading…
              </>
            ) : (
              <>
                <i className="fa-solid fa-download" />
                Download Canvas
              </>
            )}
          </button>
        </div>
        <div className="scv-header-divider" />

        {showLockedBanner && (
          <div className="scv-success-banner">
            <div className="scv-success-banner-icon">
              <CheckCircleIcon />
            </div>
            <div className="scv-success-banner-text">
              <strong>{companyName}&apos;s Strategy Canvas is locked.</strong>{" "}
              It&apos;s now read-only and ready to feed their Execution
              Blueprint.
            </div>
            <button
              type="button"
              className="scv-success-banner-close"
              onClick={() => setShowLockedBanner(false)}
              aria-label="Dismiss"
            >
              <CloseIcon size={14} />
            </button>
          </div>
        )}

        <div className="cm-container">
          <div className="cm-card">
            <div className="sc-preview-panel__body">
              {canvasModeLoading || !canvasMode ? (
                <StrategyCanvasBodySkeleton />
              ) : (
                <CanvasModeBody canvasMode={canvasMode} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategyCanvasView;
